import { slugify } from "@tellescope/utils";
import type { PortalSchema, Page, Snippet } from "./portal-schema";
import type {
  Block,
  BlockType,
  LeafBlock,
  ContainerBlockType,
  TextBlock,
  HeadingBlock,
  ButtonBlock,
  CardBlock,
  SpacerBlock,
  DividerBlock,
  BannerBlock,
  HtmlBlock,
  RichTextBlock,
  PopupBlock,
  AppointmentListBlock,
  AppointmentBookingBlock,
  OrderListBlock,
  ChatRoomListBlock,
  FileListBlock,
  FormResponseListBlock,
  PinnedFormsListBlock,
  ProfileEditBlock,
  ContentListBlock,
  PurchaseListBlock,
  FileUploadBlock,
  StripeSubscriptionsBlock,
  StripeBillingPortalButtonBlock,
  StripeInvoicesBlock,
  VitalDataBlock,
  SectionBlock,
  IframeBlock,
} from "./blocks";

// Generate a UUID v4
function generateId(): string {
  return crypto.randomUUID();
}

// Create a default snippet
export function createDefaultSnippet(key: string = "newSnippet", value: string = ""): Snippet {
  return {
    id: generateId(),
    key,
    value,
  };
}

// Create a default block of a given type
// Overloads provide type-safe returns based on input type
/* eslint-disable no-redeclare */
export function createDefaultBlock(type: LeafBlock["type"]): LeafBlock;
export function createDefaultBlock(type: ContainerBlockType): SectionBlock | IframeBlock;
export function createDefaultBlock(type: BlockType): Block;
export function createDefaultBlock(type: BlockType): Block {
  /* eslint-enable no-redeclare */
  const id = generateId();

  switch (type) {
    case "text":
      return {
        id,
        type: "text",
        props: { content: "", variant: "body" },
      } satisfies TextBlock;

    case "heading":
      return {
        id,
        type: "heading",
        props: { text: "", level: "h2" },
      } satisfies HeadingBlock;

    case "button":
      return {
        id,
        type: "button",
        props: { text: "Click me", variant: "default" },
      } satisfies ButtonBlock;

    case "card":
      return {
        id,
        type: "card",
        props: { title: "", description: "" },
      } satisfies CardBlock;

    case "spacer":
      return {
        id,
        type: "spacer",
        props: { size: "md" },
      } satisfies SpacerBlock;

    case "divider":
      return {
        id,
        type: "divider",
        props: {},
      } satisfies DividerBlock;

    case "banner":
      return {
        id,
        type: "banner",
        props: { content: "<p>Your announcement here</p>", variant: "info", dismissible: false },
      } satisfies BannerBlock;

    case "html":
      return {
        id,
        type: "html",
        props: { content: "<p>Your HTML content here</p>" },
      } satisfies HtmlBlock;

    case "rich-text":
      return {
        id,
        type: "rich-text",
        props: { content: "<p>Your formatted content here</p>" },
      } satisfies RichTextBlock;

    case "popup":
      return {
        id,
        type: "popup",
        props: {
          content: "<p>Your popup content here</p>",
          variant: "info",
          size: "default",
          dismissalBehavior: "always",
          showOnAllPages: false,
        },
      } satisfies PopupBlock;

    case "appointment-list":
      return {
        id,
        type: "appointment-list",
        props: { maxItems: 3, categories: ["all"], allowCancel: false },
      } satisfies AppointmentListBlock;

    case "appointment-booking":
      return {
        id,
        type: "appointment-booking",
        props: { maxItems: 5 },
      } satisfies AppointmentBookingBlock;

    case "order-list":
      return {
        id,
        type: "order-list",
        props: { maxItems: 5 },
      } satisfies OrderListBlock;

    case "chatroom-list":
      return {
        id,
        type: "chatroom-list",
        props: { maxItems: 5, allowNewConversation: false },
      } satisfies ChatRoomListBlock;

    case "file-list":
      return {
        id,
        type: "file-list",
        props: { maxItems: 10 },
      } satisfies FileListBlock;

    case "form-response-list":
      return {
        id,
        type: "form-response-list",
        props: { maxItems: 4, categories: ["all"] },
      } satisfies FormResponseListBlock;

    case "pinned-forms-list":
      return {
        id,
        type: "pinned-forms-list",
        props: { maxItems: 4 },
      } satisfies PinnedFormsListBlock;

    case "profile-edit":
      return {
        id,
        type: "profile-edit",
        props: {
          fields: {},
          submitButtonText: "Save Changes",
          successMessage: "Profile updated successfully",
          showResetButton: false,
        },
      } satisfies ProfileEditBlock;

    case "content-list":
      return {
        id,
        type: "content-list",
        props: { maxItems: 10, headerPhotoStyle: "banner", showSearch: false },
      } satisfies ContentListBlock;

    case "purchase-list":
      return {
        id,
        type: "purchase-list",
        props: { maxItems: 4 },
      } satisfies PurchaseListBlock;

    case "file-upload":
      return {
        id,
        type: "file-upload",
        props: { variant: "button", buttonVariant: "default", text: "Upload File" },
      } satisfies FileUploadBlock;

    case "section":
      return {
        id,
        type: "section",
        props: { gap: "md" },
        blocks: [],
      } satisfies SectionBlock;

    case "stripe-subscriptions":
      return {
        id,
        type: "stripe-subscriptions",
        props: {
          maxItems: 5,
          allowCancel: false,
          allowUndoCancel: false,
          allowPause: false,
          allowResume: false,
          allowUpdatePaymentMethod: false,
        },
      } satisfies StripeSubscriptionsBlock;

    case "stripe-billing-portal-button":
      return {
        id,
        type: "stripe-billing-portal-button",
        props: { label: "Manage Subscriptions", variant: "default" },
      } satisfies StripeBillingPortalButtonBlock;

    case "stripe-invoices":
      return {
        id,
        type: "stripe-invoices",
        props: { maxItems: 5 },
      } satisfies StripeInvoicesBlock;

    case "vital-data":
      return {
        id,
        type: "vital-data",
        props: {},
      } satisfies VitalDataBlock;

    case "iframe":
      return {
        id,
        type: "iframe",
        props: {
          url: "",
          height: "md",
          passToken: true,
          tokenParam: "token",
          sandbox: {
            allowScripts: true,
            allowSameOrigin: true,
            allowForms: true,
            allowPopups: false,
          },
        },
        fallbackBlocks: [],
      } satisfies IframeBlock;

    default: {
      const _exhaustive: never = type;
      throw new Error(`Unknown block type: ${_exhaustive}`);
    }
  }
}

// Create a default page
export function createDefaultPage(title: string = "New Page"): Page {
  const slug = slugify(title);

  return {
    id: generateId(),
    slug: slug || "new-page",
    title,
    blocks: [],
  };
}

// Create a default portal schema
export function createDefaultSchema(): PortalSchema {
  return {
    version: 1,
    name: "My Portal",
    snippets: [
      { id: generateId(), key: "supportPhone", value: "1-800-555-1234" },
      { id: generateId(), key: "officeHours", value: "Monday-Friday, 9am-5pm" },
      { id: generateId(), key: "welcomeMessage", value: "We're glad you're here!" },
      { id: generateId(), key: "emergencyLine", value: "" }, // Empty to demo fallback
      { id: generateId(), key: "alternatePhone", value: "" }, // Empty to demo chained fallback
    ],
    pages: [
      {
        id: generateId(),
        slug: "home",
        title: "Home",
        blocks: [
          {
            id: generateId(),
            type: "heading",
            props: { text: "Welcome, {{enduser.fname}}!", level: "h1" },
          },
          {
            id: generateId(),
            type: "text",
            props: {
              content:
                "Welcome to the {{organization.name}} patient portal. Here you can access your health information and communicate with your care team.",
              variant: "body",
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "md" },
          },
          {
            id: generateId(),
            type: "card",
            props: {
              title: "Template Variables Demo",
              description:
                '{{enduser.preferredName?"Hi ":"Hello "}}{{enduser.preferredName||enduser.fname||"Guest"}} {{enduser.lname}}! {{snippet.welcomeMessage}} Your email: {{enduser.email}}. Custom fields: {{enduser.Date}}. Reach us at {{snippet.emergencyLine||snippet.alternatePhone||snippet.supportPhone||"no phone available"}} during {{snippet.officeHours}}. Org: {{organization.name}}. Status: {{enduser.phone?"📱 Phone on file":"📵 No phone"}}.',
            },
          },
        ],
      },
      {
        id: generateId(),
        slug: "dashboard",
        title: "Dashboard",
        blocks: [
          {
            id: generateId(),
            type: "heading",
            props: { text: "Your Dashboard", level: "h1" },
          },
          {
            id: generateId(),
            type: "text",
            props: {
              content: "A quick overview of your health portal activity.",
              variant: "body",
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "md" },
          },
          // Two-column layout using Section blocks
          // Each section takes full width on mobile, half width on medium+ screens
          {
            id: generateId(),
            type: "section",
            props: { title: "Appointments", gap: "sm" },
            layout: { xs: 12, md: 6 },
            blocks: [
              {
                id: generateId(),
                type: "heading",
                props: { text: "Upcoming Appointments", level: "h3" },
              },
              {
                id: generateId(),
                type: "appointment-list",
                props: { maxItems: 3, categories: ["upcoming"], allowCancel: false },
              },
            ],
          },
          {
            id: generateId(),
            type: "section",
            props: { title: "Messages", gap: "sm" },
            layout: { xs: 12, md: 6 },
            blocks: [
              {
                id: generateId(),
                type: "heading",
                props: { text: "Recent Messages", level: "h3" },
              },
              {
                id: generateId(),
                type: "chatroom-list",
                props: { maxItems: 3, allowNewConversation: false },
              },
            ],
          },
          {
            id: generateId(),
            type: "section",
            props: { title: "Form Responses", gap: "sm" },
            layout: { xs: 12, md: 6 },
            blocks: [
              {
                id: generateId(),
                type: "heading",
                props: { text: "Pending Responses", level: "h3" },
              },
              {
                id: generateId(),
                type: "form-response-list",
                props: { maxItems: 3, categories: ["unsubmitted"] },
              },
            ],
          },
          {
            id: generateId(),
            type: "section",
            props: { title: "Files", gap: "sm" },
            layout: { xs: 12, md: 6 },
            blocks: [
              {
                id: generateId(),
                type: "heading",
                props: { text: "Recent Files", level: "h3" },
              },
              {
                id: generateId(),
                type: "file-list",
                props: { maxItems: 3 },
              },
            ],
          },
        ],
      },
      {
        id: generateId(),
        slug: "conditional-demo",
        title: "Conditional Demo",
        blocks: [
          {
            id: generateId(),
            type: "heading",
            props: { text: "Conditional Visibility Demo", level: "h1" },
          },
          {
            id: generateId(),
            type: "text",
            props: {
              content:
                "This page demonstrates conditional visibility - blocks that show or hide based on patient data. Try editing the enduser profile to see blocks appear and disappear.",
              variant: "body",
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "lg" },
          },

          // Section 1: Simple field equality
          {
            id: generateId(),
            type: "heading",
            props: { text: "1. Simple Field Conditions", level: "h2" },
          },
          {
            id: generateId(),
            type: "text",
            props: {
              content: "These cards show based on simple field value checks.",
              variant: "muted",
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "sm" },
          },
          // Card visible when state is "CA"
          {
            id: generateId(),
            type: "card",
            props: {
              title: "California Resident",
              description:
                "This card is only visible if your state is CA. It uses a simple equality condition: state = 'CA'",
            },
            layout: { xs: 12, md: 6 },
            visibility: {
              condition: {
                source: "enduser",
                field: "state",
                value: "CA",
              },
            },
          },
          // Card visible when state is "NY"
          {
            id: generateId(),
            type: "card",
            props: {
              title: "New York Resident",
              description:
                "This card is only visible if your state is NY. It uses a simple equality condition: state = 'NY'",
            },
            layout: { xs: 12, md: 6 },
            visibility: {
              condition: {
                source: "enduser",
                field: "state",
                value: "NY",
              },
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "lg" },
          },

          // Section 2: Field existence checks
          {
            id: generateId(),
            type: "heading",
            props: { text: "2. Field Existence Checks", level: "h2" },
          },
          {
            id: generateId(),
            type: "text",
            props: {
              content: "These blocks check whether a field has a value or not.",
              variant: "muted",
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "sm" },
          },
          // Card visible when email exists
          {
            id: generateId(),
            type: "card",
            props: {
              title: "Email Verified",
              description:
                "This card appears when you have an email on file. It uses the $exists operator to check if the email field has a value.",
            },
            layout: { xs: 12, md: 6 },
            visibility: {
              condition: {
                source: "enduser",
                field: "email",
                value: { $exists: true },
              },
            },
          },
          // Card visible when phone does NOT exist (using $not)
          {
            id: generateId(),
            type: "card",
            props: {
              title: "Phone Number Needed",
              description:
                "This card appears when you don't have a phone number. It uses $not with $exists to check for missing data.",
            },
            layout: { xs: 12, md: 6 },
            visibility: {
              condition: {
                $not: {
                  source: "enduser",
                  field: "phone",
                  value: { $exists: true },
                },
              },
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "lg" },
          },

          // Section 3: Complex AND/OR conditions
          {
            id: generateId(),
            type: "heading",
            props: { text: "3. Combined Conditions (AND/OR)", level: "h2" },
          },
          {
            id: generateId(),
            type: "text",
            props: {
              content: "These blocks use multiple conditions combined with AND/OR logic.",
              variant: "muted",
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "sm" },
          },
          // Card visible when first name is John AND state is CA
          {
            id: generateId(),
            type: "card",
            props: {
              title: "Welcome John from California!",
              description:
                "This personalized card uses $and to show only when first name is 'John' AND state is 'CA'. Both conditions must be true.",
            },
            layout: { xs: 12, md: 6 },
            visibility: {
              condition: {
                $and: [
                  { source: "enduser", field: "fname", value: "John" },
                  { source: "enduser", field: "state", value: "CA" },
                ],
              },
            },
          },
          // Card visible when state is CA OR NY
          {
            id: generateId(),
            type: "card",
            props: {
              title: "East or West Coast",
              description:
                "This card uses $or to show when state is either 'CA' OR 'NY'. Either condition being true is sufficient.",
            },
            layout: { xs: 12, md: 6 },
            visibility: {
              condition: {
                $or: [
                  { source: "enduser", field: "state", value: "CA" },
                  { source: "enduser", field: "state", value: "NY" },
                ],
              },
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "lg" },
          },

          // Section 4: Section with conditional children
          {
            id: generateId(),
            type: "heading",
            props: { text: "4. Section with Conditional Children", level: "h2" },
          },
          {
            id: generateId(),
            type: "text",
            props: {
              content:
                "This section contains child blocks with conditions. The section itself hides when ALL children are hidden.",
              variant: "muted",
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "sm" },
          },
          {
            id: generateId(),
            type: "section",
            props: { title: "Conditional Section", gap: "md" },
            blocks: [
              {
                id: generateId(),
                type: "heading",
                props: { text: "Regional Content", level: "h3" },
              },
              {
                id: generateId(),
                type: "text",
                props: {
                  content: "California clinic hours: Mon-Fri 8am-6pm",
                  variant: "body",
                },
                visibility: {
                  condition: {
                    source: "enduser",
                    field: "state",
                    value: "CA",
                  },
                },
              },
              {
                id: generateId(),
                type: "text",
                props: {
                  content: "New York clinic hours: Mon-Fri 9am-5pm",
                  variant: "body",
                },
                visibility: {
                  condition: {
                    source: "enduser",
                    field: "state",
                    value: "NY",
                  },
                },
              },
              {
                id: generateId(),
                type: "text",
                props: {
                  content: "Texas clinic hours: Mon-Sat 7am-7pm",
                  variant: "body",
                },
                visibility: {
                  condition: {
                    source: "enduser",
                    field: "state",
                    value: "TX",
                  },
                },
              },
            ],
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "lg" },
          },

          // Section 5: String operators
          {
            id: generateId(),
            type: "heading",
            props: { text: "5. String Matching", level: "h2" },
          },
          {
            id: generateId(),
            type: "text",
            props: {
              content: "These blocks use string matching operators like $regex and $in.",
              variant: "muted",
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "sm" },
          },
          // Card visible when email contains "@gmail"
          {
            id: generateId(),
            type: "card",
            props: {
              title: "Gmail User",
              description:
                "This card uses $regex to check if your email contains '@gmail'. Pattern matching with regular expressions!",
            },
            layout: { xs: 12, md: 6 },
            visibility: {
              condition: {
                source: "enduser",
                field: "email",
                value: { $regex: "@gmail" },
              },
            },
          },
          // Card visible when state is in a list
          {
            id: generateId(),
            type: "card",
            props: {
              title: "Pacific Coast States",
              description:
                "This card uses $in to check if your state is in the list ['CA', 'OR', 'WA']. Great for multi-value matching!",
            },
            layout: { xs: 12, md: 6 },
            visibility: {
              condition: {
                source: "enduser",
                field: "state",
                value: { $in: ["CA", "OR", "WA"] },
              },
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "lg" },
          },

          // Section 6: Array/Tag matching
          {
            id: generateId(),
            type: "heading",
            props: { text: "6. Tag/Array Matching", level: "h2" },
          },
          {
            id: generateId(),
            type: "text",
            props: {
              content:
                "These blocks check if a tag exists in the enduser's tags array. The $in operator works on arrays too!",
              variant: "muted",
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "sm" },
          },
          // Card visible when tags contain "Created"
          {
            id: generateId(),
            type: "card",
            props: {
              title: "Tagged: Created",
              description:
                "This card appears when your tags array contains 'Created'. Uses $in to check if a value exists in an array field.",
            },
            layout: { xs: 12, md: 6 },
            visibility: {
              condition: {
                source: "enduser",
                field: "tags",
                value: { $in: ["Created"] },
              },
            },
          },
          // Card visible when tags do NOT contain "Created"
          {
            id: generateId(),
            type: "card",
            props: {
              title: "Not Tagged: Created",
              description:
                "This card appears when your tags do NOT contain 'Created'. Uses $not with $in for negative array matching.",
            },
            layout: { xs: 12, md: 6 },
            visibility: {
              condition: {
                $not: {
                  source: "enduser",
                  field: "tags",
                  value: { $in: ["Created"] },
                },
              },
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "lg" },
          },
          {
            id: generateId(),
            type: "divider",
            props: {},
          },
          {
            id: generateId(),
            type: "text",
            props: {
              content:
                "Tip: Edit blocks in the admin panel to see how visibility conditions are configured. You can combine $and, $or, and $not for complex logic!",
              variant: "muted",
            },
          },
        ],
      },
      {
        id: generateId(),
        slug: "appointments",
        title: "Appointments",
        blocks: [
          {
            id: generateId(),
            type: "heading",
            props: { text: "Your Appointments", level: "h1" },
          },
          {
            id: generateId(),
            type: "text",
            props: {
              content: "View and manage your upcoming appointments with your care team.",
              variant: "body",
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "md" },
          },
          {
            id: generateId(),
            type: "appointment-list",
            props: { maxItems: 3, categories: ["all"], allowCancel: false },
          },
        ],
      },
      {
        id: generateId(),
        slug: "orders",
        title: "Orders",
        blocks: [
          {
            id: generateId(),
            type: "heading",
            props: { text: "Your Orders", level: "h1" },
          },
          {
            id: generateId(),
            type: "text",
            props: {
              content: "Track and view your orders and shipments.",
              variant: "body",
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "md" },
          },
          {
            id: generateId(),
            type: "order-list",
            props: { maxItems: 5 },
          },
        ],
      },
      {
        id: generateId(),
        slug: "purchases",
        title: "Purchases",
        blocks: [
          {
            id: generateId(),
            type: "heading",
            props: { text: "Your Purchases", level: "h1" },
          },
          {
            id: generateId(),
            type: "text",
            props: {
              content: "View your purchase history and payment records.",
              variant: "body",
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "md" },
          },
          {
            id: generateId(),
            type: "purchase-list",
            props: { maxItems: 4 },
          },
        ],
      },
      {
        id: generateId(),
        slug: "messages",
        title: "Messages",
        blocks: [
          {
            id: generateId(),
            type: "heading",
            props: { text: "Messages", level: "h1" },
          },
          {
            id: generateId(),
            type: "text",
            props: {
              content: "Send and receive secure messages with your healthcare providers.",
              variant: "body",
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "md" },
          },
          {
            id: generateId(),
            type: "chatroom-list",
            props: { maxItems: 5, allowNewConversation: false },
          },
        ],
      },
      {
        id: generateId(),
        slug: "files",
        title: "Files",
        blocks: [
          {
            id: generateId(),
            type: "heading",
            props: { text: "Your Files", level: "h1" },
          },
          {
            id: generateId(),
            type: "text",
            props: {
              content: "Access your documents, lab results, and other files shared by your care team.",
              variant: "body",
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "md" },
          },
          {
            id: generateId(),
            type: "file-upload",
            props: { variant: "button", buttonVariant: "default", text: "Upload File" },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "md" },
          },
          {
            id: generateId(),
            type: "file-list",
            props: { maxItems: 10 },
          },
        ],
      },
      {
        id: generateId(),
        slug: "forms",
        title: "Forms",
        blocks: [
          {
            id: generateId(),
            type: "heading",
            props: { text: "Your Forms", level: "h1" },
          },
          {
            id: generateId(),
            type: "text",
            props: {
              content: "Complete forms and view your submitted responses.",
              variant: "body",
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "md" },
          },
          // Two-column layout: Pinned forms and Form responses side by side
          {
            id: generateId(),
            type: "section",
            props: { title: "Available Forms", gap: "sm" },
            layout: { xs: 12, md: 6 },
            blocks: [
              {
                id: generateId(),
                type: "heading",
                props: { text: "Available Forms", level: "h3" },
              },
              {
                id: generateId(),
                type: "pinned-forms-list",
                props: { maxItems: 4, emptyMessage: "No forms available" },
              },
            ],
          },
          {
            id: generateId(),
            type: "section",
            props: { title: "Your Responses", gap: "sm" },
            layout: { xs: 12, md: 6 },
            blocks: [
              {
                id: generateId(),
                type: "heading",
                props: { text: "Your Responses", level: "h3" },
              },
              {
                id: generateId(),
                type: "form-response-list",
                props: { maxItems: 4, categories: ["all"] },
              },
            ],
          },
        ],
      },
      {
        id: generateId(),
        slug: "content",
        title: "Content",
        blocks: [
          {
            id: generateId(),
            type: "heading",
            props: { text: "Your Content", level: "h1" },
          },
          {
            id: generateId(),
            type: "text",
            props: {
              content: "Access educational materials, articles, and resources shared by your care team.",
              variant: "body",
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "md" },
          },
          {
            id: generateId(),
            type: "content-list",
            props: { maxItems: 10, headerPhotoStyle: "banner", showSearch: false },
          },
        ],
      },
      {
        id: generateId(),
        slug: "profile",
        title: "Profile",
        blocks: [
          {
            id: generateId(),
            type: "heading",
            props: { text: "Your Profile", level: "h1" },
          },
          {
            id: generateId(),
            type: "text",
            props: {
              content: "View and update your personal information.",
              variant: "body",
            },
          },
          {
            id: generateId(),
            type: "spacer",
            props: { size: "md" },
          },
          {
            id: generateId(),
            type: "profile-edit",
            props: {
              fields: {},
              title: "Personal Information",
              submitButtonText: "Save Changes",
              successMessage: "Profile updated successfully",
              showResetButton: false,
            },
          },
        ],
      },
    ],
    settings: {
      disableLandingPage: false,
      disableRegistration: false,
      disableSMSForMFA: false,
      integrations: {
        openLoopChat: {
          enabled: false,
        },
        season: {},
        zendesk: {
          enabled: false,
          authEnabled: false,
        },
      },
      theming: {},
      layout: {
        enableSidebarNavigation: false,
      },
    },
    updatedAt: new Date().toISOString(),
  };
}
